import { useState, useRef } from 'react';
import { 
	Star, 
	X, 
	Plus, 
	Minus,
	CheckCircle,
	AlertCircle,
	Image as ImageIcon
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useReviewStore } from '../stores/useReviewStore';

const WriteReviewForm = ({ 
	product, 
	onClose, 
	onSuccess,
	existingOrder = null 
}) => {
	const [formData, setFormData] = useState({
		title: '',
		content: '',
		ratings: {
			overall: 0,
			quality: 0,
			design: 0,
			value: 0,
			comfort: 0
		},
		pros: [''],
		cons: [''],
		isRecommended: false
	});
	
	const [hoveredStars, setHoveredStars] = useState({
		overall: 0,
		quality: 0,
		design: 0,
		value: 0,
		comfort: 0
	});
	
	const [selectedImages, setSelectedImages] = useState([]);
	const [imagePreviewUrls, setImagePreviewUrls] = useState([]);
	const [errors, setErrors] = useState({});
	const fileInputRef = useRef(null);
	
	const { createReview, uploadReviewImages, loading } = useReviewStore();

	// רנדור כוכבי דירוג
	const renderStarRating = (category, label, required = false) => {
		const currentRating = formData.ratings[category];
		const hovered = hoveredStars[category];
		
		return (
			<div className="space-y-2">
				<label className="block text-sm font-medium text-gray-300">
					{label} {required && <span className="text-red-400">*</span>}
				</label>
				<div className="flex items-center gap-1">
					{Array.from({ length: 5 }, (_, index) => {
						const starValue = index + 1;
						const isActive = starValue <= (hovered || currentRating);
						
						return (
							<button
								key={index}
								type="button"
								onMouseEnter={() => setHoveredStars(prev => ({ ...prev, [category]: starValue }))}
								onMouseLeave={() => setHoveredStars(prev => ({ ...prev, [category]: 0 }))}
								onClick={() => setFormData(prev => ({
									...prev,
									ratings: { ...prev.ratings, [category]: starValue }
								}))}
								className="transition-transform hover:scale-110"
							>
								<Star
									className={`w-6 h-6 transition-colors ${
										isActive 
											? 'text-yellow-400 fill-current' 
											: 'text-gray-400 hover:text-yellow-300'
									}`}
								/>
							</button>
						);
					})}
					<span className="mr-2 text-sm text-gray-400">
						{currentRating > 0 ? `${currentRating}.0` : 'לא דורג'}
					</span>
				</div>
			</div>
		);
	};

	// הוספת יתרון/חסרון
	const addProsOrCons = (type) => {
		setFormData(prev => ({
			...prev,
			[type]: [...prev[type], '']
		}));
	};

	// הסרת יתרון/חסרון
	const removeProsOrCons = (type, index) => {
		setFormData(prev => ({
			...prev,
			[type]: prev[type].filter((_, i) => i !== index)
		}));
	};

	// עדכון יתרון/חסרון
	const updateProsOrCons = (type, index, value) => {
		setFormData(prev => ({
			...prev,
			[type]: prev[type].map((item, i) => i === index ? value : item)
		}));
	};

	// טיפול בהעלאת תמונות
	const handleImageUpload = (event) => {
		const files = Array.from(event.target.files);
		
		if (selectedImages.length + files.length > 5) {
			setErrors(prev => ({ ...prev, images: 'ניתן להעלות עד 5 תמונות' }));
			return;
		}

		files.forEach(file => {
			if (file.size > 5 * 1024 * 1024) { // 5MB
				setErrors(prev => ({ ...prev, images: 'גודל תמונה לא יכול לעלות על 5MB' }));
				return;
			}

			if (!file.type.startsWith('image/')) {
				setErrors(prev => ({ ...prev, images: 'ניתן להעלות רק קבצי תמונה' }));
				return;
			}

			// יצירת URL לתצוגה מקדימה
			const reader = new FileReader();
			reader.onload = (e) => {
				setImagePreviewUrls(prev => [...prev, e.target.result]);
			};
			reader.readAsDataURL(file);
		});

		setSelectedImages(prev => [...prev, ...files]);
		setErrors(prev => ({ ...prev, images: null }));
	};

	// הסרת תמונה
	const removeImage = (index) => {
		setSelectedImages(prev => prev.filter((_, i) => i !== index));
		setImagePreviewUrls(prev => prev.filter((_, i) => i !== index));
	};

	// ולידציה
	const validateForm = () => {
		const newErrors = {};

		if (!formData.title.trim()) {
			newErrors.title = 'כותרת הביקורת חובה';
		}

		if (!formData.content.trim()) {
			newErrors.content = 'תוכן הביקורת חובה';
		} else if (formData.content.trim().length < 10) {
			newErrors.content = 'תוכן הביקורת חייב להכיל לפחות 10 תווים';
		}

		if (formData.ratings.overall === 0) {
			newErrors.overall = 'דירוג כללי חובה';
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	// שליחת הטופס
	const handleSubmit = async (e) => {
		e.preventDefault();
		
		if (!validateForm()) {
			return;
		}

		try {
			// יצירת הביקורת
			const reviewData = {
				productId: product._id,
				title: formData.title.trim(),
				content: formData.content.trim(),
				ratings: {
					overall: formData.ratings.overall,
					...(formData.ratings.quality && { quality: formData.ratings.quality }),
					...(formData.ratings.design && { design: formData.ratings.design }),
					...(formData.ratings.value && { value: formData.ratings.value }),
					...(formData.ratings.comfort && { comfort: formData.ratings.comfort })
				},
				pros: formData.pros.filter(pro => pro.trim()),
				cons: formData.cons.filter(con => con.trim()),
				isRecommended: formData.isRecommended,
				...(existingOrder && { orderId: existingOrder._id })
			};

			const review = await createReview(reviewData);

			// העלאת תמונות אם קיימות
			if (selectedImages.length > 0) {
				try {
					await uploadReviewImages(review._id, selectedImages);
				} catch (error) {
					console.error('Error uploading images:', error);
				}
			}

			onSuccess?.(review);
			onClose?.();

		} catch (error) {
			console.error('Error creating review:', error);
		}
	};

	return (
		<div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-md p-4">
			<motion.div
				initial={{ opacity: 0, scale: 0.9 }}
				animate={{ opacity: 1, scale: 1 }}
				className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto border" 
				style={{borderColor: 'rgba(100, 13, 70, 0.2)'}}
				dir="rtl"
			>
				{/* כותרת */}
				<div className="flex items-center justify-between mb-6">
					<div>
						<h2 className="text-2xl font-bold text-white">כתיבת ביקורת</h2>
						<p className="text-gray-400 mt-1">שתף את החוויה שלך עם {product.name}</p>
					</div>
					<button
						onClick={onClose}
						className="p-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-gray-700/50"
					>
						<X className="w-6 h-6" />
					</button>
				</div>

				{/* פרטי המוצר */}
				<div className="flex items-center gap-4 p-4 bg-gray-700/30 rounded-lg mb-6">
					<img
						src={product.imageUrl || '/placeholder.jpg'}
						alt={product.name}
						className="w-16 h-16 rounded-lg object-cover"
					/>
					<div>
						<h3 className="font-semibold text-white">{product.name}</h3>
						<p className="text-brand-primary font-medium">₪{product.price}</p>
						{existingOrder && (
							<div className="flex items-center gap-1 mt-1">
								<CheckCircle className="w-4 h-4 text-brand-primary" />
								<span className="text-xs" style={{color: 'rgba(100, 13, 70, 0.8)'}}>רכישה מאומתת</span>
							</div>
						)}
					</div>
				</div>

				<form onSubmit={handleSubmit} className="space-y-6">
					{/* דירוג כללי */}
					<div>
						{renderStarRating('overall', 'דירוג כללי', true)}
						{errors.overall && (
							<div className="flex items-center gap-1 mt-1 text-red-400 text-sm">
								<AlertCircle className="w-4 h-4" />
								{errors.overall}
							</div>
						)}
					</div>

					{/* דירוגים מפורטים */}
					<div>
						<h3 className="text-lg font-semibold text-white mb-4">דירוגים מפורטים (אופציונלי)</h3>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							{renderStarRating('quality', 'איכות')}
							{renderStarRating('design', 'עיצוב')}
							{renderStarRating('value', 'תמורה למחיר')}
							{renderStarRating('comfort', 'נוחות')}
						</div>
					</div>

					{/* כותרת */}
					<div>
						<label className="block text-sm font-medium text-gray-300 mb-2">
							כותרת הביקורת <span className="text-red-400">*</span>
						</label>
						<input
							type="text"
							value={formData.title}
							onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
							placeholder="כותרת קצרה ותמציתית..."
							className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-brand-primary focus:outline-none transition-colors"
							maxLength={100}
						/>
						{errors.title && (
							<div className="flex items-center gap-1 mt-1 text-red-400 text-sm">
								<AlertCircle className="w-4 h-4" />
								{errors.title}
							</div>
						)}
					</div>

					{/* תוכן הביקורת */}
					<div>
						<label className="block text-sm font-medium text-gray-300 mb-2">
							תוכן הביקורת <span className="text-red-400">*</span>
						</label>
						<textarea
							value={formData.content}
							onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
							placeholder="ספר על החוויה שלך עם המוצר..."
							rows={5}
							className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-brand-primary focus:outline-none transition-colors resize-none"
							maxLength={1000}
						/>
						<div className="flex justify-between items-center mt-1">
							{errors.content && (
								<div className="flex items-center gap-1 text-red-400 text-sm">
									<AlertCircle className="w-4 h-4" />
									{errors.content}
								</div>
							)}
							<div className="text-xs text-gray-400 mr-auto">
								{formData.content.length}/1000
							</div>
						</div>
					</div>

					{/* יתרונות */}
					<div>
						<label className="block text-sm font-medium text-gray-300 mb-2">יתרונות</label>
						<div className="space-y-2">
							{formData.pros.map((pro, index) => (
								<div key={index} className="flex items-center gap-2">
									<div className="flex-1 flex items-center gap-2">
										<span className="text-green-400 text-sm">✓</span>
										<input
											type="text"
											value={pro}
											onChange={(e) => updateProsOrCons('pros', index, e.target.value)}
											placeholder="יתרון..."
											className="flex-1 px-3 py-2 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-brand-primary focus:outline-none transition-colors"
											maxLength={100}
										/>
									</div>
									{formData.pros.length > 1 && (
										<button
											type="button"
											onClick={() => removeProsOrCons('pros', index)}
											className="p-2 text-red-400 hover:text-red-300 transition-colors"
										>
											<Minus className="w-4 h-4" />
										</button>
									)}
								</div>
							))}
							{formData.pros.length < 5 && (
								<button
									type="button"
									onClick={() => addProsOrCons('pros')}
									className="flex items-center gap-2 px-3 py-2 text-green-400 hover:text-green-300 transition-colors"
								>
									<Plus className="w-4 h-4" />
									<span className="text-sm">הוסף יתרון</span>
								</button>
							)}
						</div>
					</div>

					{/* חסרונות */}
					<div>
						<label className="block text-sm font-medium text-gray-300 mb-2">חסרונות</label>
						<div className="space-y-2">
							{formData.cons.map((con, index) => (
								<div key={index} className="flex items-center gap-2">
									<div className="flex-1 flex items-center gap-2">
										<span className="text-red-400 text-sm">✗</span>
										<input
											type="text"
											value={con}
											onChange={(e) => updateProsOrCons('cons', index, e.target.value)}
											placeholder="חסרון..."
											className="flex-1 px-3 py-2 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-brand-primary focus:outline-none transition-colors"
											maxLength={100}
										/>
									</div>
									{formData.cons.length > 1 && (
										<button
											type="button"
											onClick={() => removeProsOrCons('cons', index)}
											className="p-2 text-red-400 hover:text-red-300 transition-colors"
										>
											<Minus className="w-4 h-4" />
										</button>
									)}
								</div>
							))}
							{formData.cons.length < 5 && (
								<button
									type="button"
									onClick={() => addProsOrCons('cons')}
									className="flex items-center gap-2 px-3 py-2 text-red-400 hover:text-red-300 transition-colors"
								>
									<Plus className="w-4 h-4" />
									<span className="text-sm">הוסף חסרון</span>
								</button>
							)}
						</div>
					</div>

					{/* תמונות */}
					<div>
						<label className="block text-sm font-medium text-gray-300 mb-2">תמונות (עד 5)</label>
						
						{/* תצוגת תמונות קיימות */}
						{imagePreviewUrls.length > 0 && (
							<div className="grid grid-cols-3 gap-2 mb-4">
								{imagePreviewUrls.map((url, index) => (
									<div key={index} className="relative">
										<img
											src={url}
											alt={`תמונה ${index + 1}`}
											className="w-full aspect-square object-cover rounded-lg"
										/>
										<button
											type="button"
											onClick={() => removeImage(index)}
											className="absolute -top-2 -left-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
										>
											<X className="w-3 h-3" />
										</button>
									</div>
								))}
							</div>
						)}

						{/* כפתור העלאה */}
						{selectedImages.length < 5 && (
							<div>
								<input
									ref={fileInputRef}
									type="file"
									multiple
									accept="image/*"
									onChange={handleImageUpload}
									className="hidden"
								/>
								<button
									type="button"
									onClick={() => fileInputRef.current?.click()}
									className="flex items-center gap-2 px-4 py-3 border-2 border-dashed border-gray-600 rounded-lg text-gray-400 text-brand-primary-hover transition-colors w-full justify-center"
									style={{'&:hover': {borderColor: 'var(--brand-primary)'}}}
								>
									<ImageIcon className="w-5 h-5" />
									<span>בחר תמונות</span>
								</button>
							</div>
						)}

						{errors.images && (
							<div className="flex items-center gap-1 mt-1 text-red-400 text-sm">
								<AlertCircle className="w-4 h-4" />
								{errors.images}
							</div>
						)}
					</div>

					{/* האם ממליץ */}
					<div className="flex items-center gap-3">
						<input
							type="checkbox"
							id="recommended"
							checked={formData.isRecommended}
							onChange={(e) => setFormData(prev => ({ ...prev, isRecommended: e.target.checked }))}
							className="w-5 h-5 border-gray-600 rounded focus:ring-brand-primary focus:ring-2"
							style={{accentColor: 'var(--brand-primary)'}}
						/>
						<label htmlFor="recommended" className="text-gray-300 font-medium">
							אני ממליץ על המוצר הזה
						</label>
					</div>

					{/* כפתורי פעולה */}
					<div className="flex items-center gap-4 pt-4 border-t border-gray-700">
						<button
							type="submit"
							disabled={loading}
							className="flex-1 bg-brand-primary text-white px-6 py-3 rounded-lg font-semibold bg-brand-primary-hover transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
						>
							{loading ? 'שולח...' : 'פרסם ביקורת'}
						</button>
						
						<button
							type="button"
							onClick={onClose}
							className="px-6 py-3 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-700/50 transition-colors"
						>
							ביטול
						</button>
					</div>
				</form>
			</motion.div>
		</div>
	);
};

export default WriteReviewForm; 